import { useState } from "react";
import { AICC_SANDBOX_ACTION } from "constants/aiccPromptSandbox";
import { AiccSandboxGrammarResult, AiccSandboxHintResult, AiccSandboxTurn } from "types/model/aiccPromptSandbox";
import { AiccSandboxActionRequest } from "types/dto/request/aiccPromptSandbox";
import * as sandboxAPI from "services/aiccPromptSandbox";
import { SandboxRequestBase } from "../sandboxRequest";

interface Options {
  languageId?: number;
  requestBase: () => SandboxRequestBase;
  transcript: AiccSandboxTurn[];
  hint?: Pick<AiccSandboxActionRequest, "variant" | "variables">;
}

export function useSandboxActions({ languageId, requestBase, transcript, hint: hintRequest }: Options) {
  // 🔴 `undefined` is not asked, `null` is asked and the flow answered nothing.
  const [hint, setHint] = useState<string | null>();
  const [isHinting, setIsHinting] = useState(false);
  const [corrections, setCorrections] = useState<Record<number, AiccSandboxGrammarResult | null>>({});
  const [correctingIndex, setCorrectingIndex] = useState<number>();

  const askForHint = () => {
    if (!languageId) {
      return;
    }

    setIsHinting(true);
    sandboxAPI
      .runAiccSandboxAction(languageId, AICC_SANDBOX_ACTION.hint, {
        ...requestBase(),
        ...hintRequest,
        variables: { ...requestBase().variables, ...hintRequest?.variables },
        transcript,
      })
      .then(({ result }) => setHint(result ? (result as AiccSandboxHintResult).hint : null))
      .catch(() => undefined)
      .finally(() => setIsHinting(false));
  };

  const correct = (index: number, userText: string) => {
    if (!languageId) {
      return;
    }

    setCorrectingIndex(index);
    sandboxAPI
      .runAiccSandboxAction(languageId, AICC_SANDBOX_ACTION.errorCorrection, {
        ...requestBase(),
        transcript,
        user_text: userText,
      })
      .then(({ result }) =>
        setCorrections((current) => ({ ...current, [index]: (result as AiccSandboxGrammarResult) ?? null }))
      )
      .catch(() => undefined)
      .finally(() => setCorrectingIndex(undefined));
  };

  const clearActions = () => {
    setHint(undefined);
    setCorrections({});
  };

  return {
    hint,
    isHinting,
    askForHint,
    dismissHint: () => setHint(undefined),
    corrections,
    correctingIndex,
    correct,
    clearActions,
  };
}
